"use client";

import { TARGET_GROUP_FACET } from "@lib/constants/filtersConstants";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { scrollToTopAfterFilter } from "@lib/utils/scrollToTop";
import { useStore } from "@store/index";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { type FC, useEffect, useState } from "react";
import { useInstantSearch, useRefinementList } from "react-instantsearch";
import styles from "./ProductCategoryFilter.module.css";
import { Button } from "@ecco/ui/button";

const PRODUCT_CATEGORY_FILTER_NAME = "Target Group";

type ProductCategoryFilterTabProps = {
    label: string;
    isActive: boolean;
    onClick: () => void;
};

const ProductCategoryFilterTab: FC<ProductCategoryFilterTabProps> = ({
    label,
    isActive,
    onClick,
}) => (
    <Button
        variant="ghost"
        className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
        onClick={onClick}
        aria-current={isActive ? "true" : undefined}
    >
        <Text
            as="span"
            fontSize={["mobileBodyTextSmall", "mobileBodyTextSmall", "tabletBodyB1"]}
            className={[styles.label, isActive ? styles.labelActive : ""].filter(Boolean).join(" ")}
        >
            {label}
        </Text>
    </Button>
);

export const ProductCategoryFilter = () => {
    const { get: getMicrocopy } = useMicrocopy();
    const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);
    const allLabel = getMicrocopy(CommonCMS.page, CommonCMS.PLP.targetGroupAll) ?? "All";
    const targetGroupsToHide =
        getMicrocopy(CommonCMS.shared, CommonCMS.algolia.hideTargetGroup) ?? "";

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const { refine: toggleFilter, items } = useRefinementList({
        attribute: TARGET_GROUP_FACET,
        limit: 100,
        sortBy: ["name"],
    });

    const { indexUiState } = useInstantSearch();
    const refinedTargetGroups = indexUiState.refinementList?.[TARGET_GROUP_FACET] ?? [];

    const hiddenGroups = new Set(
        targetGroupsToHide
            .split(",")
            .map((group) => group.trim())
            .filter(Boolean)
    );

    const availableItems = items.filter(
        (item) => (item.count > 0 || item.isRefined) && !hiddenGroups.has(item.value)
    );

    const isTabActive = (value: string | null) => {
        if (!isMounted) {
            return value === null;
        }
        return value === null
            ? refinedTargetGroups.length === 0
            : refinedTargetGroups.includes(value);
    };

    const tabs = [
        {
            label: allLabel,
            value: null,
        },
        ...availableItems,
    ];

    const selectValue = (value: string | null) => {
        items.forEach((item) => {
            const shouldClearRefinement = item.isRefined && item.value !== value;
            const shouldApplyRefinement = item.value === value && !item.isRefined;

            if (shouldClearRefinement) {
                toggleFilter(item.value);
            }

            if (shouldApplyRefinement) {
                toggleFilter(item.value);
                AnalyticsClient.listFilterApplied(
                    PRODUCT_CATEGORY_FILTER_NAME,
                    item.value,
                    TARGET_GROUP_FACET
                );
            }
        });

        setLastLoadedPage(1);
        scrollToTopAfterFilter();
    };

    if (availableItems.length === 0 && refinedTargetGroups.length === 0) {
        return null;
    }

    return (
        <Box
            data-testid="product-category-filter"
            className={styles.container}
            pt={4.8}
            pb={6}
            px={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
        >
            <nav
                className={styles.list}
                aria-label={PRODUCT_CATEGORY_FILTER_NAME}
                data-testid={"product-category-filters"}
            >
                {tabs.map((item) => (
                    <ProductCategoryFilterTab
                        key={item.value ?? "all"}
                        label={item.label}
                        isActive={isTabActive(item.value)}
                        onClick={() => selectValue(item.value)}
                    />
                ))}
            </nav>
        </Box>
    );
};

ProductCategoryFilter.displayName = "ProductCategoryFilter";
